// Globals
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { createProduct, productsSelector } from '../../feature';

// Modules
import { getPartnersList } from '@/modules/Partners';
import { CategorySelect, useCategoriesSelect } from '@/modules/SharedCategories';
import { getCategories } from '@/modules/Categories';

// Models
import { ROUTES } from '@/models/router.model';

// Components
import {
  StyledHeaderBlock,
  StyledContainerTitle,
  StyledContainerWrapper,
  StyledContentBlock,
  StyledSaveChangesButton,
  StyledHeaderLeftSideBlock,
  StyledHeaderRightSideBlock,
  StyledDivider,
  StyledForm,
  StyledHintText,
} from './CreateProductContainerStyles';
import { showErrorToast, showSuccessToast, useForm } from '@/components';
import { CreateProductForm, ProductFiltersBlock } from '../../components';

// Helper
import { CreateNewProductDTO } from '../../dtos';
import {
  CreateProductFormFieldsNames,
  ProductCharacteristicsFormFieldsNames,
  TProductCharacteristicsFormValues,
  getProductFiltersQueryParams,
  getStoresOptions,
} from '../../helpers';
import { useProductFilters, useStoresSearch } from '../../hooks';
import { isValidateFormErrorEntity } from '@/utils';

const initialFiltersValues: TProductCharacteristicsFormValues = {
  category: '',
};

const CreateProductContainer = () => {
  const { t } = useTranslation(['products']);
  const { isLoading } = useAppState(productsSelector);
  const { storeList, searchStores } = useStoresSearch();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [mainInfoForm] = useForm();
  const [filtersForm] = useForm();

  const { categories, autocompleteCategories, onSearch, expandedItems, handleExpandTreeItemData } =
    useCategoriesSelect();
  const { filters, handleCategoryChange } = useProductFilters(null);

  const handleSubmit = useCallback(async () => {
    try {
      const mainInfoValues = await mainInfoForm.validateFields();
      const filtersValues = await filtersForm.validateFields();
      const mainInfoRequestData = new CreateNewProductDTO(mainInfoValues);
      const filtersRequestData = getProductFiltersQueryParams(filtersValues, filters);

      const newProduct = await dispatch(
        createProduct({ mainInfoData: mainInfoRequestData, filtersData: filtersRequestData }),
      ).unwrap();
      if (newProduct?.data?.id) {
        navigate(`${ROUTES.PRODUCTS}/${newProduct?.data?.id}`);
      }
      showSuccessToast({ message: t('data_saved') });
    } catch (error) {
      if (isValidateFormErrorEntity(error)) {
        const missingLangs = error.errorFields.map((f) => f.name[0]);

        if (missingLangs.includes(CreateProductFormFieldsNames.LOCALE)) {
          showErrorToast({ message: t('product_update_validation_locale_error') });
        }
      }
      showErrorToast({ message: t('product_update_validation_error') });
    }
  }, [filters]);

  useEffect(() => {
    dispatch(getCategories());
    dispatch(getPartnersList({ limit: 10 }));
  }, []);

  useEffect(() => {
    if (filters.length > 0) {
      const initialFilters: Record<string, string | string[]> = {};
      filters.forEach((filter) => {
        const selectedValues = filter.values.filter((value) => value.selected).map((value) => value.id);
        initialFilters[filter.id] = selectedValues.length > 1 ? selectedValues : selectedValues[0];
      });
      filtersForm.setFieldsValue(initialFilters);
    }
  }, [filters]);

  return (
    <StyledContainerWrapper>
      <StyledHeaderBlock>
        <StyledHeaderLeftSideBlock>
          <StyledContainerTitle>{t('create_product_title')}</StyledContainerTitle>
        </StyledHeaderLeftSideBlock>
        <StyledHeaderRightSideBlock>
          <StyledSaveChangesButton htmlType="submit" onClick={handleSubmit} disabled={isLoading}>
            {t('product_details_save_changes_button')}
          </StyledSaveChangesButton>
        </StyledHeaderRightSideBlock>
      </StyledHeaderBlock>
      <StyledContentBlock>
        <CreateProductForm
          formInstance={mainInfoForm}
          handleSubmit={handleSubmit}
          storesOptions={getStoresOptions(storeList ?? [])}
          onStoresSearch={searchStores}
        />
        <StyledDivider />
        <StyledForm initialValues={initialFiltersValues} onFinish={handleSubmit} form={filtersForm}>
          <CategorySelect
            categories={categories}
            autocompleteCategories={autocompleteCategories}
            expandedItems={expandedItems}
            handleExpandTreeItemData={handleExpandTreeItemData}
            onSearch={onSearch}
            formItemName={ProductCharacteristicsFormFieldsNames.CATEGORY}
            handleCategoryChange={handleCategoryChange}
          />
          <StyledHintText>{t('product_category_hint')}</StyledHintText>
          <ProductFiltersBlock filters={filters} />
        </StyledForm>
      </StyledContentBlock>
    </StyledContainerWrapper>
  );
};

export default CreateProductContainer;
